import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { Decision, Project, ScheduleTask } from '../types'
import { addBusinessDays, daysBetween, fmtDate } from '../lib/format'
import Badge from '../components/Badge'

export default function DecisionsTab({ project }: { project: Project }) {
  const [decisions, setDecisions] = useState<Decision[]>([])
  const [tasks, setTasks] = useState<ScheduleTask[]>([])

  useEffect(() => {
    api.decisions.list(project.id).then(setDecisions)
    api.schedule.list(project.id).then(setTasks)
  }, [project.id])

  // Compute deadline = (phase start) - lead_weeks*7 days.
  const enriched = useMemo(() => {
    if (!project.start_date) return decisions.map((d) => ({ d, deadline: null as Date | null }))
    const phaseStart = new Map<string, Date>()
    let cursor = new Date(project.start_date)
    const seen = new Set<string>()
    for (const t of tasks) {
      if (!seen.has(t.phase)) {
        phaseStart.set(t.phase, new Date(cursor))
        seen.add(t.phase)
      }
      cursor = addBusinessDays(cursor, t.duration_business_days)
    }
    return decisions.map((d) => {
      const ps = d.phase_needed_for ? phaseStart.get(d.phase_needed_for) : null
      let deadline: Date | null = null
      if (ps && d.lead_weeks) {
        deadline = new Date(ps)
        deadline.setDate(deadline.getDate() - d.lead_weeks * 7)
      }
      return { d, deadline }
    })
  }, [decisions, tasks, project.start_date])

  const sorted = useMemo(
    () => [...enriched].sort((a, b) => (a.deadline?.getTime() ?? Infinity) - (b.deadline?.getTime() ?? Infinity)),
    [enriched]
  )

  const overdue = sorted.filter((x) => !x.d.completed && x.deadline && x.deadline < new Date()).length
  const soon = sorted.filter((x) => {
    if (x.d.completed || !x.deadline) return false
    const days = daysBetween(new Date(), x.deadline) ?? 999
    return days >= 0 && days <= 14
  }).length
  const done = sorted.filter((x) => x.d.completed).length

  async function toggle(d: Decision) {
    const updated = await api.decisions.update(d.id, { completed: d.completed ? 0 : 1 } as Partial<Decision>)
    setDecisions((prev) => prev.map((x) => (x.id === d.id ? updated : x)))
  }

  return (
    <section>
      <h2>Decision Deadlines</h2>
      <p className="muted small mb-3">{decisions.length} decisions. Click to toggle complete.</p>

      <div className="row gap-2 mb-3">
        <Badge color="red">{overdue} OVERDUE</Badge>
        <Badge color="gold">{soon} SOON</Badge>
        <Badge color="green">{done} DONE</Badge>
      </div>

      <div className="card">
        {sorted.map(({ d, deadline }) => {
          const days = deadline ? daysBetween(new Date(), deadline) : null
          const overdue = !d.completed && days !== null && days < 0
          const soon = !d.completed && days !== null && days >= 0 && days <= 14
          return (
            <div
              key={d.id}
              className={`row-list ${d.completed ? 'done' : overdue ? 'overdue' : soon ? 'soon' : ''}`}
              onClick={() => toggle(d)}
            >
              <span className={`chk ${d.completed ? 'on' : ''}`}>{d.completed ? '✓' : ''}</span>
              <div className="grow">
                <div className="row-title">{d.decision_name}</div>
                <div className="muted small">
                  {d.phase_needed_for ?? '—'} · {d.lead_weeks ?? '—'}wk lead
                </div>
              </div>
              <div className="text-right">
                <div className="row-date">{deadline ? fmtDate(deadline.toISOString()) : '—'}</div>
                <div className="muted small">
                  {d.completed ? 'Done' : days === null ? '' : days < 0 ? `${Math.abs(days)}d late` : `${days}d`}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
